import React, { useContext, useMemo, useState } from 'react';

import { MenuItem, Select, makeStyles } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import cx from 'classnames';
import { TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL } from 'config/urls';
import { debounce } from 'lodash';
import { map } from 'lodash';

import { ReactComponent as FilterIcon } from 'pages/Teachers/ClassroomInsights/assets/filter-icon.svg';
import { ReactComponent as SearchIcon } from 'pages/Teachers/ClassroomInsights/assets/search-icon.svg';
import CheckGradeIcon from 'pages/Teachers/ClassroomInsights/components/CheckGradeIcon';
import { MASTERY_LEVELS } from 'pages/Teachers/ClassroomInsights/constants';
import { getGradeLabel } from 'pages/Teachers/ClassroomInsights/utils';
import { UNABLE_TO_DIAGNOSE } from 'pages/Teachers/Insights/constants';
import { trackMixpanelEvent } from 'utils/integrations/mixpanel';

import UserContext from 'components/UserContext';

import styles from './style.module.scss';

const masteryOptions = [
  MASTERY_LEVELS.ABOVE_MASTERY,
  MASTERY_LEVELS.AT_MASTERY,
  MASTERY_LEVELS.BELOW_MASTERY,
  MASTERY_LEVELS.NEEDS_REVIEW,
  'All Levels'
];

const masteryLevelOptions = map(masteryOptions, (level, index) => ({
  key: index,
  value: level,
  label: level
}));

const orderedMasteryLevelOptions = masteryLevelOptions.sort(
  (a, b) => a.key - b.key
);

const useStyles = makeStyles((theme) => ({
  selectRoot: {
    height: '36px',
    backgroundColor: '#F8F7FF',
    borderRadius: '10px',
    border: '1.5px solid #E0D9F7',
    padding: '4px 12px',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: '#D1C4E9',
      backgroundColor: '#F3F0FF'
    },
    '&:before, &:after': {
      display: 'none'
    }
  },
  selectIcon: {
    color: '#6A61A1',
    right: '12px'
  },
  inputText: {
    color: '#6A61A1',
    fontSize: '1.1rem',
    fontWeight: 400,
    fontFamily: 'inherit',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  placeholder: {
    color: '#7B74AB',
    opacity: 0.8
  }
}));

const StudentsSearch = ({
  students,
  sectionId,
  nameValue,
  levelValue,
  gradeValue,
  submissionStatusValue,
  onStudentSelect,
  onLevelSelect,
  onGradeSelect,
  onSubmissionStatusSelect,
  objectiveValueType
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(students);
  const [open, setOpen] = useState(false);
  const [selectedStudentsIds, setSelectedStudentsIds] = useState(nameValue);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [openSubmissions, setOpenSubmissions] = useState(false);

  const [openMastery, setOpenMastery] = useState(false);
  const [openGrade, setOpenGrade] = useState(false);

  const { user } = useContext(UserContext);

  const classes = useStyles();

  const runSearch = useMemo(
    () =>
      debounce((value) => {
        const v = value.toLowerCase();
        setResults(
          students.filter(
            (s) =>
              s.name.toLowerCase().includes(v) &&
              !selectedStudentsIds.includes(s.student_id)
          )
        );
        setOpen(true);
      }, 300),
    [students, selectedStudentsIds]
  );

  const onChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    runSearch(value);
  };

  const handleSelect = (student) => {
    const newSelected = [...selectedStudentsIds, student.student_id];
    setSelectedStudentsIds(newSelected);
    setQuery('');
    setResults([]);
    setOpen(false);
    onStudentSelect(newSelected);

    trackMixpanelEvent(user.id, 'Search by name used.', {
      feature: 'live classroom insights',
      view: 'student progress',
      class_id: sectionId,
      normalizedURL:
        process.env.REACT_APP_BASE_FRONTEND_URL +
        TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
    });
  };

  const handleRemove = (studentId) => {
    const newSelected = selectedStudentsIds.filter((s) => s !== studentId);
    setSelectedStudentsIds(newSelected);
    onStudentSelect(newSelected);
  };

  const selectedStudents = useMemo(
    () => students?.filter((s) => selectedStudentsIds.includes(s.student_id)),
    [students, selectedStudentsIds]
  );

  const availableGrades = useMemo(() => {
    const grades = students
      ?.filter((s) => s.grade !== null)
      .map((s) => s.grade);

    return [...new Set(grades)].sort().concat('All Grades');
  }, [students]);

  return (
    <div
      className={cx(styles.container, {
        [styles.noOptionsSelected]: selectedStudentsIds.length === 0
      })}
    >
      <div className={styles.searchWrapper}>
        <div className={styles.searchInput}>
          <IconButton
            className={styles.filterBtn}
            onClick={() => {
              setFiltersOpen((open) => !open);
              setOpen(false);
            }}
          >
            <FilterIcon />
          </IconButton>

          <input
            type="text"
            placeholder="Search Students here..."
            value={query}
            onChange={onChange}
          />

          <SearchIcon />
        </div>

        {open && (
          <div className={styles.results}>
            {results.length === 0 && (
              <div className={cx(styles.result, styles.empty)}>
                No students found
              </div>
            )}
            {results.map((student, index) => (
              <div
                key={index}
                className={styles.result}
                onClick={() => handleSelect(student)}
              >
                {student.name}
              </div>
            ))}
          </div>
        )}

        {filtersOpen && (
          <div className={styles.filters}>
            <Select
              open={openMastery}
              onClose={() => setOpenMastery(false)}
              onOpen={() => setOpenMastery(true)}
              value={levelValue || 'All Levels'}
              onChange={(level) => {
                if (level.target.value === 'All Levels') {
                  onLevelSelect(null);
                  setFiltersOpen(false);
                  trackMixpanelEvent(user.id, 'Filter cleared', {
                    feature: 'live classroom insights',
                    view: 'student progress',
                    filter_type: 'mastery level',
                    class_id: sectionId,
                    normalizedURL:
                      process.env.REACT_APP_BASE_FRONTEND_URL +
                      TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
                  });
                } else {
                  onLevelSelect(level.target.value);
                  setFiltersOpen(false);
                  trackMixpanelEvent(user.id, 'Filter applied', {
                    feature: 'live classroom insights',
                    view: 'student progress',
                    filter_type: 'mastery level',
                    filter_value: level.target.value,
                    class_id: sectionId,
                    normalizedURL:
                      process.env.REACT_APP_BASE_FRONTEND_URL +
                      TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
                  });
                }
              }}
              IconComponent={ExpandMoreIcon}
              className={classes.selectRoot}
              classes={{ select: classes.inputText, icon: classes.selectIcon }}
            >
              {orderedMasteryLevelOptions.map(({ value, label, key }) => {
                if (value === UNABLE_TO_DIAGNOSE) {
                  return null;
                }

                return (
                  <MenuItem
                    key={key}
                    value={value}
                    className={classes.menuItem}
                  >
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
            <Select
              open={openGrade}
              onClose={() => setOpenGrade(false)}
              onOpen={() => setOpenGrade(true)}
              value={gradeValue || 'All Grades'}
              onChange={(grade) => {
                if (grade.target.value === 'All Grades') {
                  onGradeSelect(null);
                  setFiltersOpen(false);
                  trackMixpanelEvent(user.id, 'Filter cleared', {
                    feature: 'live classroom insights',
                    view: 'student progress',
                    filter_type: 'grade',
                    class_id: sectionId,
                    normalizedURL:
                      process.env.REACT_APP_BASE_FRONTEND_URL +
                      TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
                  });
                } else {
                  onGradeSelect(grade.target.value);
                  setFiltersOpen(false);
                  trackMixpanelEvent(user.id, 'Filter applied', {
                    feature: 'live classroom insights',
                    view: 'student progress',
                    filter_type: 'grade',
                    filter_value: grade.target.value,
                    class_id: sectionId,
                    normalizedURL:
                      process.env.REACT_APP_BASE_FRONTEND_URL +
                      TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
                  });
                }
              }}
              IconComponent={ExpandMoreIcon}
              className={classes.selectRoot}
              classes={{
                select: classes.inputText,
                icon: classes.selectIcon
              }}
            >
              {availableGrades.map((opt) => (
                <MenuItem key={opt} value={opt} className={classes.menuItem}>
                  <CheckGradeIcon
                    grade={opt}
                    objectiveValueType={objectiveValueType}
                  />{' '}
                  {getGradeLabel({ grade: opt, objectiveValueType })}
                </MenuItem>
              ))}
            </Select>

            <Select
              open={openSubmissions}
              onClose={() => setOpenSubmissions(false)}
              onOpen={() => setOpenSubmissions(true)}
              value={submissionStatusValue || 'All Statuses'}
              onChange={(status) => {
                if (status.target.value === 'All Statuses') {
                  onSubmissionStatusSelect(null);
                  setFiltersOpen(false);
                  trackMixpanelEvent(user.id, 'Filter cleared', {
                    feature: 'live classroom insights',
                    view: 'student progress',
                    filter_type: 'submission status',
                    class_id: sectionId,
                    normalizedURL:
                      process.env.REACT_APP_BASE_FRONTEND_URL +
                      TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
                  });
                } else {
                  onSubmissionStatusSelect(status.target.value);
                  setFiltersOpen(false);
                  trackMixpanelEvent(user.id, 'Filter applied', {
                    feature: 'live classroom insights',
                    view: 'student progress',
                    filter_type: 'submission status',
                    filter_value: status.target.value,
                    class_id: sectionId,
                    normalizedURL:
                      process.env.REACT_APP_BASE_FRONTEND_URL +
                      TEACHER_ASSIGNMENT_CLASSROOM_INSIGHTS_URL
                  });
                }
              }}
              IconComponent={ExpandMoreIcon}
              className={classes.selectRoot}
              classes={{
                select: classes.inputText,
                icon: classes.selectIcon
              }}
            >
              <MenuItem
                key="all"
                value="All Statuses"
                className={classes.menuItem}
              >
                All Statuses
              </MenuItem>
              <MenuItem
                key="submitted"
                value="submitted"
                className={classes.menuItem}
              >
                Submitted
              </MenuItem>
              <MenuItem
                key="pending"
                value="not_submitted"
                className={classes.menuItem}
              >
                Pending Upload
              </MenuItem>
            </Select>
          </div>
        )}
      </div>
      <div className={styles.selectedContainer}>
        {selectedStudents?.map((student, index) => (
          <div key={student.index} className={styles.selectedItem}>
            {student.name}
            <span
              className={styles.removeBtn}
              onClick={() => handleRemove(student.student_id)}
            >
              ×
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StudentsSearch;
